import { useState } from 'react';
import { printDocument } from '@/components/documents';
import { SearchSelect } from '@/components/SearchSelect';
import {
  ErrorNote,
  Field,
  OkNote,
  Select,
  Table,
  Tile,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatCount, formatDate, formatMoney, formatQuantity } from '@/format';
import {
  ACCOUNT_TYPE_LABELS,
  useBalanceSheet,
  useBuyerStatement,
  useCostPerBird,
  useLedger,
  usePostableAccounts,
  useProfitAndLoss,
  useTrialBalance,
} from '@/hooks/finance';
import { useBuyers } from '@/hooks/sales';
import { useAuth } from '@/stores/auth';

type Report =
  | 'trialBalance'
  | 'profitAndLoss'
  | 'balanceSheet'
  | 'ledger'
  | 'statement'
  | 'costPerBird';

const REPORTS: Array<{ value: Report; label: string; permission: string }> = [
  { value: 'trialBalance', label: 'Trial balance', permission: 'fin.report.read' },
  { value: 'profitAndLoss', label: 'Profit and loss', permission: 'fin.report.read' },
  { value: 'balanceSheet', label: 'Balance sheet', permission: 'fin.report.read' },
  { value: 'ledger', label: 'Account ledger', permission: 'fin.ledger.read' },
  { value: 'statement', label: 'Buyer statement', permission: 'fin.statement.read' },
  { value: 'costPerBird', label: 'Cost per bird', permission: 'fin.margin.read' },
];

function monthStart(): string {
  return `${todayIso().slice(0, 7)}-01`;
}

/**
 * Financials › Reports.
 *
 * One screen with a report picker rather than six tabs, because the choices a
 * person makes — which dates, which farm — are the same for all of them, and six
 * tabs would ask for them six times.
 *
 * Every report says whether it balances where balancing is the point. A trial
 * balance that does not is the loudest thing this module could tell anybody, so it
 * is not hidden behind a total somebody has to add up themselves.
 */
export function FinanceReports() {
  const can = useAuth((s) => s.can);
  const allowed = REPORTS.filter((r) => can(r.permission));

  const [report, setReport] = useState<Report>(allowed[0]?.value ?? 'trialBalance');
  const [asOn, setAsOn] = useState(todayIso());
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const [accountCode, setAccountCode] = useState('');
  const [buyerId, setBuyerId] = useState('');

  const accounts = usePostableAccounts();
  const buyers = useBuyers();

  const trial = useTrialBalance(report === 'trialBalance' ? asOn : '');
  const pnl = useProfitAndLoss(
    report === 'profitAndLoss' ? from : '',
    report === 'profitAndLoss' ? to : '',
  );
  const sheet = useBalanceSheet(report === 'balanceSheet' ? asOn : '');
  const ledger = useLedger({
    ...(report === 'ledger' && accountCode ? { accountCode } : {}),
    from,
    to,
  });
  const statement = useBuyerStatement(report === 'statement' ? buyerId : '', from, to);
  const cost = useCostPerBird();

  if (allowed.length === 0) {
    return <ErrorNote message="You cannot see any of the financial reports." />;
  }

  const error =
    trial.error ?? pnl.error ?? sheet.error ?? ledger.error ?? statement.error ?? cost.error;

  function print(title: string, facts: Array<[string, string]>) {
    printDocument({ title, facts, showCost: true });
  }

  return (
    <div className="stack">
      <div className="toolbar toolbar--wrap">
        <Select
          label="Report"
          value={report}
          onChange={(v) => setReport(v as Report)}
          options={allowed.map((r) => ({ value: r.value, label: r.label }))}
          required
        />
        {(report === 'trialBalance' || report === 'balanceSheet') && (
          <Field label="As at" type="date" value={asOn} onChange={setAsOn} />
        )}
        {(report === 'profitAndLoss' ||
          report === 'ledger' ||
          report === 'statement') && (
          <>
            <Field label="From" type="date" value={from} onChange={setFrom} />
            <Field label="To" type="date" value={to} onChange={setTo} />
          </>
        )}
        {report === 'ledger' && (
          <Select
            label="Account"
            value={accountCode}
            onChange={setAccountCode}
            options={(accounts.data?.items ?? []).map((a) => ({
              value: a.code,
              label: `${a.code} — ${a.nameEn}`,
            }))}
            required
          />
        )}
        {report === 'statement' && (
          <SearchSelect
            label="Buyer"
            value={buyerId}
            onChange={(option) => setBuyerId(option?.value ?? '')}
            options={(buyers.data?.items ?? []).map((b) => ({
              value: b.id,
              label: b.nameEn,
            }))}
            required
          />
        )}
      </div>

      {error && <ErrorNote message={errorMessage(error, 'That report could not be read')} />}

      {report === 'trialBalance' && trial.data && (
        <section className="panel">
          <div className="formpanel__head">
            <h2>Trial balance as at {formatDate(trial.data.asOn)}</h2>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() =>
                print(`Trial balance as at ${formatDate(trial.data!.asOn)}`, [
                  ['Total debits', formatMoney(trial.data!.totalDebit)],
                  ['Total credits', formatMoney(trial.data!.totalCredit)],
                  ['Balanced', trial.data!.balanced ? 'Yes' : 'NO'],
                ])
              }
            >
              Print
            </button>
          </div>
          {trial.data.balanced ? (
            <OkNote message="Debits and credits agree." />
          ) : (
            <ErrorNote
              message={
                `The two sides do not agree: debits ${formatMoney(trial.data.totalDebit)}, ` +
                `credits ${formatMoney(trial.data.totalCredit)}. Nothing should be able to ` +
                'do this — tell whoever maintains the system.'
              }
            />
          )}
          <Table
            count={trial.data.items.length}
            empty="Nothing has been posted yet."
            head={
              <tr>
                <th>Account</th>
                <th>Kind</th>
                <th className="num">Debit</th>
                <th className="num">Credit</th>
              </tr>
            }
          >
            {trial.data.items.map((row) => (
              <tr key={row.code}>
                <td>
                  {row.code} — {row.nameEn}
                </td>
                <td>{ACCOUNT_TYPE_LABELS[row.type] ?? row.type}</td>
                <td className="num">
                  {row.closingDebit === '0.00' ? '' : formatMoney(row.closingDebit)}
                </td>
                <td className="num">
                  {row.closingCredit === '0.00' ? '' : formatMoney(row.closingCredit)}
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={2}>
                <strong>Total</strong>
              </td>
              <td className="num">
                <strong>{formatMoney(trial.data.totalDebit)}</strong>
              </td>
              <td className="num">
                <strong>{formatMoney(trial.data.totalCredit)}</strong>
              </td>
            </tr>
          </Table>
        </section>
      )}

      {report === 'profitAndLoss' && pnl.data && (
        <section className="panel">
          <h2>
            Profit and loss, {formatDate(pnl.data.from)} to {formatDate(pnl.data.to)}
          </h2>
          <div className="tiles">
            <Tile label="Earned" value={formatMoney(pnl.data.totalIncome)} note="Income" />
            <Tile label="Spent" value={formatMoney(pnl.data.totalExpense)} note="Costs" />
            <Tile
              label={Number(pnl.data.netResult) < 0 ? 'Loss' : 'Profit'}
              value={formatMoney(pnl.data.netResult)}
              note="Earned less spent"
              tone={Number(pnl.data.netResult) < 0 ? 'alert' : undefined}
            />
          </div>
          <Table
            count={pnl.data.items.length}
            empty="Nothing was earned or spent over these days."
            head={
              <tr>
                <th>Account</th>
                <th>Group</th>
                <th>Kind</th>
                <th className="num">Amount</th>
              </tr>
            }
          >
            {pnl.data.items.map((row) => (
              <tr key={row.code}>
                <td>
                  {row.code} — {row.nameEn}
                </td>
                <td>{row.groupName ?? ''}</td>
                <td>{row.type === 'INCOME' ? 'Earned' : 'Spent'}</td>
                <td className="num">{formatMoney(row.closingBalance)}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}

      {report === 'balanceSheet' && sheet.data && (
        <section className="panel">
          <h2>Balance sheet as at {formatDate(sheet.data.asOn)}</h2>
          {!sheet.data.balanced && (
            <ErrorNote message="The two sides do not agree. Tell whoever maintains the system." />
          )}
          <div className="tiles">
            <Tile label="What we own" value={formatMoney(sheet.data.totalAssets)} note="Assets" />
            <Tile
              label="What we owe"
              value={formatMoney(sheet.data.totalLiabilities)}
              note="Liabilities"
            />
            <Tile
              label="Capital"
              value={formatMoney(sheet.data.totalEquity)}
              note={`Including ${formatMoney(sheet.data.resultNotYetRolled)} earned this year`}
            />
          </div>
          {(
            [
              ['What we own', sheet.data.assets],
              ['What we owe', sheet.data.liabilities],
              ['Capital', sheet.data.equity],
            ] as const
          ).map(([title, items]) => (
            <div key={title}>
              <h3>{title}</h3>
              <Table
                count={items.length}
                empty="Nothing here."
                head={
                  <tr>
                    <th>Account</th>
                    <th>Group</th>
                    <th className="num">Amount</th>
                  </tr>
                }
              >
                {items.map((row) => (
                  <tr key={row.code}>
                    <td>
                      {row.code} — {row.nameEn}
                    </td>
                    <td>{row.groupName ?? ''}</td>
                    <td className="num">{formatMoney(row.closingBalance)}</td>
                  </tr>
                ))}
              </Table>
            </div>
          ))}
        </section>
      )}

      {report === 'ledger' && (
        <section className="panel">
          <h2>{accountCode ? `Ledger for ${accountCode}` : 'Pick an account'}</h2>
          {ledger.data && (
            <>
              <div className="tiles">
                <Tile
                  label="Opening balance"
                  value={formatMoney(ledger.data.openingBalance)}
                  note={`Everything before ${formatDate(ledger.data.from)}`}
                />
                <Tile
                  label="Closing balance"
                  value={formatMoney(ledger.data.closingBalance)}
                  note={`As at ${formatDate(ledger.data.to)}`}
                />
              </div>
              <p className="muted">
                The running balance is worked out as this page is drawn, so an entry
                made for an earlier day corrects the whole column.
              </p>
              <Table
                count={ledger.data.items.length}
                empty="Nothing moved on this account over these days."
                head={
                  <tr>
                    <th>Date</th>
                    <th>Entry</th>
                    <th>Narration</th>
                    <th className="num">Debit</th>
                    <th className="num">Credit</th>
                    <th className="num">Balance</th>
                  </tr>
                }
              >
                {ledger.data.items.map((row, i) => (
                  <tr key={`${row.entryNo}-${i}`}>
                    <td>{formatDate(row.entryDate)}</td>
                    <td>{row.sourceNo ?? row.entryNo}</td>
                    <td>{row.narration}</td>
                    <td className="num">{row.debit === '0.00' ? '' : formatMoney(row.debit)}</td>
                    <td className="num">{row.credit === '0.00' ? '' : formatMoney(row.credit)}</td>
                    <td className="num">{formatMoney(row.closingBalance)}</td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </section>
      )}

      {report === 'statement' && (
        <section className="panel">
          <div className="formpanel__head">
            <h2>{statement.data ? `Statement — ${statement.data.buyer.nameEn}` : 'Pick a buyer'}</h2>
            {statement.data && (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() =>
                  print(`Statement — ${statement.data!.buyer.nameEn}`, [
                    ['From', formatDate(statement.data!.from)],
                    ['To', formatDate(statement.data!.to)],
                    ['Opening balance', formatMoney(statement.data!.openingBalance)],
                    ['Billed', formatMoney(statement.data!.periodDebit)],
                    ['Received', formatMoney(statement.data!.periodCredit)],
                    ['Closing balance', formatMoney(statement.data!.closingBalance)],
                  ])
                }
              >
                Print
              </button>
            )}
          </div>
          {statement.data && (
            <Table
              count={statement.data.items.length}
              empty="Nothing moved on this account over these days."
              head={
                <tr>
                  <th>Date</th>
                  <th>Document</th>
                  <th>Narration</th>
                  <th className="num">Billed</th>
                  <th className="num">Received</th>
                  <th className="num">Owes</th>
                </tr>
              }
            >
              {statement.data.items.map((row, i) => (
                <tr key={`${row.entryNo}-${i}`}>
                  <td>{formatDate(row.entryDate)}</td>
                  <td>{row.sourceNo ?? row.entryNo}</td>
                  <td>{row.narration}</td>
                  <td className="num">{row.debit === '0.00' ? '' : formatMoney(row.debit)}</td>
                  <td className="num">{row.credit === '0.00' ? '' : formatMoney(row.credit)}</td>
                  <td className="num">{formatMoney(row.closingBalance)}</td>
                </tr>
              ))}
            </Table>
          )}
        </section>
      )}

      {report === 'costPerBird' && cost.data && (
        <section className="panel">
          <h2>What each flock cost</h2>
          <p className="muted">
            Costs carrying the flock, over birds placed. There is no work-in-progress
            account, so this figure and the profit and loss can never disagree.
          </p>
          <Table
            count={cost.data.items.length}
            empty="No flocks yet."
            head={
              <tr>
                <th>Flock</th>
                <th className="num">Birds placed</th>
                <th className="num">Birds sold</th>
                <th className="num">Weight sold</th>
                <th className="num">Total cost</th>
                <th className="num">Per bird</th>
                <th className="num">Per kg</th>
                <th className="num">Margin</th>
              </tr>
            }
          >
            {cost.data.items.map((row) => (
              <tr key={row.flockId}>
                <td>
                  {row.flockCode} — {row.flockName}
                </td>
                <td className="num">{formatCount(row.birdsPlaced)}</td>
                <td className="num">
                  {row.soldBirds === null ? '—' : formatCount(row.soldBirds)}
                </td>
                <td className="num">
                  {row.soldWeightKg === null ? '—' : formatQuantity(row.soldWeightKg, 3)}
                </td>
                <td className="num">{formatMoney(row.totalCost)}</td>
                <td className="num">
                  {row.costPerBird ? formatMoney(row.costPerBird) : '—'}
                </td>
                <td className="num">{row.costPerKg ? formatMoney(row.costPerKg) : '—'}</td>
                <td className="num">{row.margin ? formatMoney(row.margin) : '—'}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}
    </div>
  );
}
